import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Card, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@src/components/chakra-ui/menu';
import { useAdvisorAgreementPdfDownload } from '@src/components/sco/Advisors/hooks/useAdvisorAgreementPdfDownload';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { DocumensoSignEmbed } from '@src/components/sco/Advisors/shared/DocumensoSignEmbed';
import { SectionHeader } from '@src/components/sco/Advisors/shared/SectionHeader';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { AGREEMENT_STATUS_DECLINED, AGREEMENT_STATUS_SENT, AGREEMENT_STATUS_SIGNED } from '@src/components/sco/Advisors/utils/agreementKeys';
import { advisorDisplayName, formatAgreementSigners } from '@src/components/sco/Advisors/utils/agreementKeys';
import { canDeleteUnsignedAdvisorAgreement, DELETE_UNSIGNED_AGREEMENT_CONFIRM } from '@src/components/sco/Advisors/utils/agreementSend';
import { cleanErrorMessage, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { uploadDocumentFile } from '@src/components/sco/UploadDocumentButton';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { DELETE_ADVISOR_AGREEMENT } from '@src/gql/mutations/DeleteAdvisorAgreement';
import { MARK_ADVISOR_AGREEMENT_SIGNED } from '@src/gql/mutations/MarkAdvisorAgreementSigned';
import { SYNC_ADVISOR_AGREEMENT_SIGNATURE } from '@src/gql/mutations/SyncAdvisorAgreementSignature';
import { ADVISOR_AGREEMENTS } from '@src/gql/queries/AdvisorAgreements';
import { ScoAdvisorAgreement, ScoAdvisorAgreementCollection, SyncAdvisorAgreementSignaturePayload } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LuCheck, LuCircleCheck, LuDownload, LuExternalLink, LuRefreshCw, LuSettings, LuTrash2, LuUpload } from 'react-icons/lu';
const submittedStorageKey = (agreementId: string) => `sco.advisors.sign.submitted.${agreementId}`;
const PAGE_SIZE = 50;
type AgreementsData = {
  advisorAgreements?: ScoAdvisorAgreementCollection | null;
};

/** Variables must match the live queries exactly or `refetchQueries` refetches nothing. */
const agreementsVariables = (role: 'FOUNDER' | 'ADVISOR') => ({
  role,
  statusKeys: [AGREEMENT_STATUS_SENT],
  pageSize: PAGE_SIZE,
  page: 1
});
const counterpartName = (row: ScoAdvisorAgreement) => row.currentUserRole === 'advisor' ? row.founderName?.trim() || personName(row.founderUser, 'Founder') : personName(row.advisorUser, row.advisorName || 'Advisor');
const documentHeading = (row: ScoAdvisorAgreement) => {
  const other = counterpartName(row);
  const equity = row.equityPercent != null ? `${row.equityPercent}% equity` : null;
  const company = row.company?.name || null;
  const bits = [equity, company].filter(Boolean);
  const signers = formatAgreementSigners({
    founderName: row.founderName?.trim() || personName(row.founderUser, 'Founder'),
    advisorName: advisorDisplayName(row),
    founderHasSigned: row.founderHasSigned,
    advisorHasSigned: row.advisorHasSigned
  });
  return {
    title: `FAST agreement with ${other}`,
    detail: [...bits, signers].join(' · ')
  };
};
export const SignSection = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const focusId = typeof router.query.agreementId === 'string' ? router.query.agreementId : '';
  useAdvisorsToolbar({
    breadcrumbs: advisorsBreadcrumbsWithTrail({
      title: 'Sign',
      href: paths.sign
    })
  });
  const founderQuery = useQuery<AgreementsData>(ADVISOR_AGREEMENTS, {
    variables: agreementsVariables('FOUNDER'),
    fetchPolicy: 'network-only'
  });
  const advisorQuery = useQuery<AgreementsData>(ADVISOR_AGREEMENTS, {
    variables: agreementsVariables('ADVISOR'),
    fetchPolicy: 'network-only'
  });
  const founderLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: founderQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: founderQuery.data?.advisorAgreements?.items ?? [],
    hasNextPage: founderQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const advisorLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: advisorQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: advisorQuery.data?.advisorAgreements?.items ?? [],
    hasNextPage: advisorQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const [markSigned] = useMutation(MARK_ADVISOR_AGREEMENT_SIGNED);
  const [syncSignature] = useMutation(SYNC_ADVISOR_AGREEMENT_SIGNATURE);
  const [deleteAgreement] = useMutation(DELETE_ADVISOR_AGREEMENT);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const pdf = useAdvisorAgreementPdfDownload();
  const attachInputRef = useRef<HTMLInputElement>(null);
  const rows = useMemo(() => {
    const founder = founderLoadMore.items;
    const advisor = advisorLoadMore.items;
    const seen = new Set<string>();
    return [...founder, ...advisor].filter((item): item is ScoAdvisorAgreement => {
      const id = item?.advisorAgreementId;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      if (item.statusKey === AGREEMENT_STATUS_SENT) {
        return true;
      }
      return Boolean(focusId && id === focusId);
    });
  }, [advisorLoadMore.items, focusId, founderLoadMore.items]);
  const listedActive = useMemo(() => {
    if (focusId) {
      return rows.find(row => row.advisorAgreementId === focusId) ?? null;
    }
    if (rows.length === 1) {
      return rows[0];
    }
    return null;
  }, [focusId, rows]);
  const [pinned, setPinned] = useState<ScoAdvisorAgreement | null>(null);
  useEffect(() => {
    if (listedActive) {
      setPinned(listedActive);
    }
  }, [listedActive]);
  const active = listedActive ?? (pinned && (!focusId || pinned.advisorAgreementId === focusId) ? pinned : null);
  useEffect(() => {
    const agreementId = active?.advisorAgreementId;
    if (!agreementId) {
      return;
    }
    try {
      if (sessionStorage.getItem(submittedStorageKey(agreementId)) === '1') {
        setSubmittedId(agreementId);
      }
    } catch {
      // private mode / blocked storage
    }
  }, [active?.advisorAgreementId]);
  const markSubmitted = (agreementId_0: string) => {
    setSubmittedId(agreementId_0);
    try {
      sessionStorage.setItem(submittedStorageKey(agreementId_0), '1');
    } catch {
      // private mode / blocked storage
    }
  };
  const hadSigningUrlFor = useRef<string | null>(null);
  useEffect(() => {
    const agreementId_1 = active?.advisorAgreementId;
    if (!agreementId_1) {
      return;
    }
    if (active.documensoSigningUrl) {
      hadSigningUrlFor.current = agreementId_1;
      return;
    }
    if (active.documensoDocumentId && hadSigningUrlFor.current === agreementId_1) {
      markSubmitted(agreementId_1);
    }
  }, [active?.advisorAgreementId, active?.documensoDocumentId, active?.documensoSigningUrl]);
  const handleCheckStatus = async (agreementId_2: string) => {
    setError(null);
    setStatusNote(null);
    setBusyId(agreementId_2);
    try {
      const result = await syncSignature({
        variables: {
          input: {
            advisorAgreementId: agreementId_2
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('FOUNDER')
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('ADVISOR')
        }]
      });
      const next = (result.data as {
        syncAdvisorAgreementSignature?: SyncAdvisorAgreementSignaturePayload | null;
      } | null)?.syncAdvisorAgreementSignature?.advisorAgreement;
      if (next?.statusKey === AGREEMENT_STATUS_SIGNED) {
        markSubmitted(agreementId_2);
        setStatusNote('Both parties signed in Documenso. The signed PDF is attached.');
      } else if (next?.statusKey === AGREEMENT_STATUS_DECLINED) {
        setStatusNote('Documenso shows this envelope was declined.');
      } else {
        setStatusNote('Still waiting in Documenso. Sign, then check status again.');
      }
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not check Documenso status.'));
    } finally {
      setBusyId(null);
    }
  };
  const handleDelete = async (agreementId_3: string) => {
    if (!window.confirm(DELETE_UNSIGNED_AGREEMENT_CONFIRM)) {
      return;
    }
    setError(null);
    setStatusNote(null);
    setBusyId(agreementId_3);
    try {
      await deleteAgreement({
        variables: {
          input: {
            advisorAgreementId: agreementId_3
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('FOUNDER')
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('ADVISOR')
        }]
      });
      setPinned(null);
      setSubmittedId(null);
    } catch (err_0) {
      setError(cleanErrorMessage(err_0, 'Could not delete this agreement.'));
    } finally {
      setBusyId(null);
    }
  };
  const handleMarkSigned = async (agreementId_4: string, file?: File | null) => {
    setError(null);
    setStatusNote(null);
    setBusyId(agreementId_4);
    try {
      let signedStoredFileId: string | undefined;
      if (file) {
        signedStoredFileId = await new Promise<string>((resolve, reject) => {
          void uploadDocumentFile({
            file,
            storedFile: {
              attachmentType: 'AdvisorAgreement',
              attachmentId: agreementId_4,
              contextKey: 'advisor-agreement-signed'
            },
            onSuccess: (_url, storedFileId) => {
              if (!storedFileId) {
                reject(new Error('Upload did not return a file id'));
                return;
              }
              resolve(storedFileId);
            },
            onError: err_2 => reject(err_2 instanceof Error ? err_2 : new Error(String(err_2)))
          });
        });
      }
      await markSigned({
        variables: {
          input: {
            advisorAgreementId: agreementId_4,
            signedStoredFileId
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('FOUNDER')
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: agreementsVariables('ADVISOR')
        }]
      });
    } catch (err_1) {
      setError(cleanErrorMessage(err_1, 'Could not mark this agreement signed.'));
    } finally {
      setBusyId(null);
    }
  };
  const loading = founderQuery.loading || advisorQuery.loading;
  const queryError = founderQuery.error || advisorQuery.error;
  const heading = active ? documentHeading(active) : null;
  const id_0 = active?.advisorAgreementId;
  const signatureSubmitted = Boolean(id_0 && submittedId === id_0) || active?.statusKey === AGREEMENT_STATUS_SIGNED || Boolean(active?.documensoDocumentId && !active.documensoSigningUrl);
  if (!active) {
    return <SectionScroll maxWidth={'3xl'}>
        <SectionHeader description={'Pick an agreement waiting for your signature.'} eyebrow={'Advisors · Manage'} title={'Sign'} />
        {error || pdf.error ? <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
            {error || pdf.error}
          </Text> : null}
        <AdvisorsQueryState error={queryError} loading={loading && rows.length === 0}>
          {rows.length === 0 ? <Text color={'colorPalette.fg/60'} fontFamily={'longform'}>
              No sent agreements waiting to be signed.
            </Text> : <VStack align={'stretch'} gap={3}>
              {rows.map(row_0 => {
            const rowId = row_0.advisorAgreementId as string;
            const copy = documentHeading(row_0);
            return <Card.Root key={rowId} variant={'subtle'}>
                    <Card.Body>
                      <HStack align={'center'} gap={3} justify={'space-between'} wrap={'wrap'}>
                        <VStack align={'flex-start'} gap={0} minWidth={0}>
                          <Text fontWeight={'semibold'}>{copy.title}</Text>
                          <Text color={'colorPalette.fg/55'} fontSize={'sm'}>
                            {copy.detail}
                          </Text>
                        </VStack>
                        <Button asChild size={'sm'} variant={'cta'}>
                          <NextLink href={paths.withQuery(paths.sign, {
                      agreementId: rowId
                    })}>Open</NextLink>
                        </Button>
                      </HStack>
                    </Card.Body>
                  </Card.Root>;
          })}
              <AdvisorsLoadMore hasNextPage={founderLoadMore.hasNextPage || advisorLoadMore.hasNextPage} loading={founderLoadMore.loadingMore || advisorLoadMore.loadingMore} onLoadMore={() => {
            if (founderLoadMore.hasNextPage) {
              void founderLoadMore.loadMore();
            }
            if (advisorLoadMore.hasNextPage) {
              void advisorLoadMore.loadMore();
            }
          }} />
            </VStack>}
        </AdvisorsQueryState>
      </SectionScroll>;
  }
  return <VStack align={'stretch'} gap={0} height={'full'} minHeight={0} width={'full'}>
      <HStack align={'center'} borderBottomWidth={'1px'} flexShrink={0} gap={3} justify={'space-between'} paddingX={{
      base: 4,
      md: 5
    }} paddingY={3} wrap={'wrap'}>
        <VStack align={'flex-start'} gap={0} minWidth={0}>
          <Text fontWeight={'semibold'} lineClamp={1}>
            {heading?.title}
          </Text>
          <Text color={'colorPalette.fg/55'} fontSize={'sm'} lineClamp={1}>
            {heading?.detail}
            {statusNote ? ` · ${statusNote}` : ''}
          </Text>
          {error || pdf.error ? <Text color={'red.fg'} fontSize={'sm'}>
              {error || pdf.error}
            </Text> : null}
        </VStack>
        {id_0 ? <>
            <input ref={attachInputRef} hidden accept={'application/pdf'} type={'file'} onChange={e => {
          const file_0 = e.target.files?.[0];
          e.target.value = '';
          if (file_0) {
            void handleMarkSigned(id_0, file_0);
          }
        }} />
            <MenuRoot positioning={{
          placement: 'bottom-end'
        }}>
              <MenuTrigger asChild>
                <Button loading={busyId === id_0 || pdf.busyId === id_0} size={'sm'} variant={'outline'}>
                  <LuSettings /> Manage
                </Button>
              </MenuTrigger>
              <MenuContent minW={'14rem'}>
                {active.documensoSigningUrl ? <MenuItem asChild value={'open-documenso'}>
                    <a href={active.documensoSigningUrl} rel={'noreferrer'} target={'_blank'}>
                      <LuExternalLink /> Open in Documenso
                    </a>
                  </MenuItem> : null}
                {active.documensoDocumentId ? <MenuItem value={'check-status'} onClick={() => void handleCheckStatus(id_0)}>
                    <LuRefreshCw /> Check status
                  </MenuItem> : null}
                <MenuItem value={'download-pdf'} onClick={() => void pdf.downloadAgreementPdf(id_0, 'generated')}>
                  <LuDownload /> Download FAST PDF
                </MenuItem>
                <MenuItem value={'attach-pdf'} onClick={() => attachInputRef.current?.click()}>
                  <LuUpload /> Attach signed PDF
                </MenuItem>
                <MenuItem value={'mark-signed'} onClick={() => void handleMarkSigned(id_0)}>
                  <LuCheck /> Mark signed
                </MenuItem>
                {canDeleteUnsignedAdvisorAgreement({
              currentUserRole: active.currentUserRole,
              statusKey: active.statusKey
            }) ? <>
                    <MenuSeparator />
                    <MenuItem color={'fg.error'} value={'delete'} onClick={() => void handleDelete(id_0)}>
                      <LuTrash2 /> Delete
                    </MenuItem>
                  </> : null}
              </MenuContent>
            </MenuRoot>
          </> : null}
      </HStack>

      <Box display={'flex'} flex={1} flexDirection={'column'} height={'100%'} minHeight={0} overflow={'hidden'} width={'full'}>
        {signatureSubmitted ? <VStack align={'center'} flex={1} gap={4} justify={'center'} padding={8} textAlign={'center'}>
            <Icon color={'green.fg'} size={'2xl'}>
              <LuCircleCheck />
            </Icon>
            <Text fontSize={'lg'} fontWeight={'semibold'}>
              Signature submitted!
            </Text>
            <Button asChild variant={'cta'}>
              <NextLink href={paths.agreements}>View All Agreements</NextLink>
            </Button>
          </VStack> : active.documensoSigningUrl ? <DocumensoSignEmbed fill showFallbackLink={false} signingUrl={active.documensoSigningUrl} onError={message => setError(message)} onCompleted={() => {
        if (id_0) {
          markSubmitted(id_0);
          void handleCheckStatus(id_0);
        }
      }} onRejected={() => {
        if (id_0) {
          void handleCheckStatus(id_0);
        }
      }} /> : <VStack align={'flex-start'} flex={1} gap={2} justify={'center'} padding={6}>
            <Text color={'colorPalette.fg/60'} fontFamily={'longform'}>
              {active.documensoDocumentId ? 'No signing link for this envelope. Use Open in Documenso from email, or Check status after you sign.' : 'No Documenso envelope on this agreement. Confirm both parties signed, then mark signed.'}
            </Text>
          </VStack>}
      </Box>
    </VStack>;
};